import React, { useEffect, useState } from "react";
import { Card, CardBody, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FlatService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import DeleteModal from "../../Common/DeleteModal";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { IFlat } from "../../shared/interfaces/flat/IFlat";
import { usePagination } from "../../shared/hooks/usePagination";
import { CustomPagination } from "../../shared/components/CustomPagination";
import { entriesPerPageOptions } from "../customers/List";

const PER_PAGE_DEFAULT = 20;

const FlatList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState<IFlat[] | null>(null);
  const [flatToDelete, setFlatToDelete] = useState<number | null>(null);

  const {
    page,
    handlePages,
    pagination,
    totalPages,
    setTotalPages,
    handleUpdateList,
    changeFilterHandler,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({
    perPage: PER_PAGE_DEFAULT,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteClick = (id: number) => {
    setFlatToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (flatToDelete !== null) {
      try {
        await FlatService.delete(flatToDelete);
        SuccessToast(t("flat.successDelete"));
        setShowDeleteModal(false);
        setFlatToDelete(null);
        handleUpdateList();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  useEffect(() => {
    FlatService.getAll(pagination)
      .then((response) => {
        const data = response.data;
        setList(data);
        setTotalPages(response.totalPages);
      })
      .catch(ErrorToast);
  }, [pagination]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody>
            <div className="text-end">
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate("/flats/create")}
              >
                <i className="ti ti-plus f-18"></i> {t("flat.add")}
              </button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t("customer.entriesPerPage")}
                    options={entriesPerPageOptions}
                    onChange={(selected) =>
                      changeFilterHandler(
                        "perPage",
                        selected?.value ?? PER_PAGE_DEFAULT,
                      )
                    }
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === PER_PAGE_DEFAULT,
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t("flat.entriesPerPage")}</span>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item">
                  <form className="">
                    <input
                      className="form-control"
                      type="search"
                      placeholder={t("flat.searchPlaceholder")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        changeFilterHandler("term", e.target.value)
                      }
                    />
                  </form>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover" id="pc-dt-simple-1">
                <thead>
                  <tr>
                    <th>{t("flat.id")}</th>
                    <th>{t("flat.flatName")}</th>
                    <th>{t("flat.floor")}</th>
                    <th>{t("floor.location")}</th>
                    <th>{t("flat.updatedAt")}</th>
                    <th className="text-end">{t("flat.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list?.map((item: IFlat) => (
                    <tr key={item?.id}>
                      <td>{item.id}</td>
                      <td>{item.name}</td>
                      <td>{item?.floor?.name}</td>
                      <td>
                        {item?.location?.address}, {item?.location?.city},{" "}
                        {item?.location?.zip}
                      </td>
                      <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                      <td className="text-end">
                        <ul className="list-inline mb-0">
                          <li
                            className="list-inline-item"
                            onClick={() => navigate(`/flats/edit/${item.id}`)}
                          >
                            <a
                              href="#"
                              className="avtar avtar-s btn-link-success btn-pc-default"
                            >
                              <i className="ti ti-edit f-20"></i>
                            </a>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              handlePages={handlePages}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
            />
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t("flat.name")}
      />
    </React.Fragment>
  );
};

export default FlatList;
